'use client';

import { usePathname, useSearchParams } from 'next/navigation';
import { useEffect, useMemo } from 'react';

import AccessKeyErrorModal from '@/components/AccessKeyErrorModal';
import BannersBoxItem from '@/components/BannersBox/BannersBoxItem';
import useCalculateInvoiceAndHoldIsPending from '@/containers/VoyagePlanner/Summary/hooks/useCalculateInvoiceAndHoldIsPending';
import { getIsAccessKeyStateValid, selectAccessKeySlice } from '@/ducks/accessKeys';
import { ACCESS_KEY_PARAM } from '@/ducks/accessKeys/constants';
import { resetAKFlowFully } from '@/ducks/accessKeys/utils';
import { useUIResourcePool } from '@/ducks/common/resources';
import { routes } from '@/ducks/routes';
import { useAppDispatch, useAppSelector } from '@/store';
import tagmanager from '@/tagmanager';

const MARQUEE_UI_RESOURCE_POOL = {
  clearCTA: 'AccessKey.clearCTA',
  defaultDescription: 'AccessKey.bannerMessage',
  defaultTitle: 'AccessKey.eyebrow.label',
  mobileClearText: 'AccessKey.clearCTA.short',
  promoIcon: 'AccessKey.emoji.key',
  promoText: 'AccessKey.bookShareToast',
  title: 'AccessKey.bannerTitle',
} as const;

type TProps = {
  className?: string;
  isErrorModalAllowed?: boolean;
  placeholder?: React.ReactNode;
};

const AccessKeyBanner = ({ className, isErrorModalAllowed, placeholder }: TProps) => {
  const dispatch = useAppDispatch();
  const hasAKParam = useSearchParams()?.has(ACCESS_KEY_PARAM);
  const accessKeyDetails = useAppSelector(selectAccessKeySlice);
  const { error, isDataReady, shortDescription, state, title } = accessKeyDetails;
  const isValid = getIsAccessKeyStateValid(state);
  const canFollowPending = usePathname() === routes.planner.summary.path;
  const isPending = useCalculateInvoiceAndHoldIsPending(true);
  const messages = useUIResourcePool(MARQUEE_UI_RESOURCE_POOL);

  useEffect(() => {
    if (isDataReady && !canFollowPending && error && !isErrorModalAllowed) {
      dispatch(resetAKFlowFully());
    }
  }, [canFollowPending, error, isErrorModalAllowed, isDataReady]);

  const canUseSkeleton = useMemo(() => hasAKParam && !isDataReady, [isDataReady, hasAKParam]);

  if (error && isErrorModalAllowed) return <AccessKeyErrorModal />;

  const clearAccessKey = () => {
    const buttonText = messages.clearCTA;
    tagmanager.tracker.common.trackIconClick({
      labels: [buttonText],
    });
    dispatch(resetAKFlowFully());
  };

  if (!isValid && !canUseSkeleton) return placeholder;

  return (
    <BannersBoxItem
      className={className}
      clearCTALabel={messages.clearCTA}
      clearDisabled={canFollowPending && isPending}
      icon={messages.promoIcon}
      isLoading={!isValid && canUseSkeleton}
      onClearClick={clearAccessKey}
      shortDescription={shortDescription ?? messages.defaultDescription}
      title={title ?? messages.defaultTitle}
    />
  );
};

export default AccessKeyBanner;
